import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Result } from 'antd';
import { useAuthStore } from '@/store/authStore';
import { usePermissionStore } from '@/store/permissionStore';
import { authApi, roleApi } from '@/api';
import { showToast, logger } from '@/shared/utils';
import { REDIRECT_URLS, UI_TEXT } from '../constants';

/**
 * SSO Callback Page
 *
 * Handles redirect from backend after successful SSO authentication
 *
 * Flow:
 * 1. Backend sets HttpOnly cookies (accessToken, refreshToken)
 * 2. Backend redirects to: /sso-callback?success=true
 * 3. This component:
 *    - Fetches user info from /me endpoint (uses cookies)
 *    - Updates auth store
 *    - Redirects to dashboard
 *
 * Error Handling:
 * - If cookies missing or /me fails → Show error and redirect to login
 * - If success=false in URL → Show error and redirect to login
 */
const SSOCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authStore = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading',
  );
  const [errorMessage, setErrorMessage] = useState<string>('');
  const hasExecutedRef = useRef(false); // Prevent duplicate execution in React Strict Mode

  const redirectToLogin = useCallback(() => {
    navigate(REDIRECT_URLS.LOGIN_PAGE, { replace: true });
  }, [navigate]);

  const redirectToDashboard = useCallback(() => {
    navigate(REDIRECT_URLS.DEFAULT_AFTER_LOGIN, { replace: true });
  }, [navigate]);

  useEffect(() => {
    // Prevent duplicate execution in React Strict Mode
    if (hasExecutedRef.current) {
      return;
    }
    hasExecutedRef.current = true;

    const handleSSOCallback = async () => {
      try {
        // Check URL params for success flag from backend redirect
        const success = searchParams.get('success');

        if (success !== 'true') {
          // No success flag - might be error or invalid access
          setStatus('error');
          setErrorMessage('Invalid callback - please login again');
          return;
        }

        // Fetch user info from /me endpoint
        // Backend already set accessToken in HttpOnly cookie
        const response = await authApi.getCurrentUser();
        const userData = response.user;

        if (!userData) {
          throw new Error('No user data received');
        }

        // Update auth store
        authStore.login(userData);

        // Load permissions and menu for user's role (same as normal login)
        try {
          const roleCode = userData.role || 'U'; // Default to 'U' (User) if no role

          const permissionData = await roleApi.getPermissionsByRole(roleCode);
          usePermissionStore
            .getState()
            .setPermissions(
              permissionData.roleCode,
              permissionData.permissions,
              permissionData.allowedFunctions,
              permissionData.allowedNodeIds,
              permissionData.menuList,
              permissionData.isAdmin,
            );
        } catch (error) {
          // Log error but don't fail login - user can still access with limited permissions
          logger.error('Failed to load permissions (SSO)', error as Error);
          // Set empty permissions - will restrict access (isAdmin = false)
          usePermissionStore
            .getState()
            .setPermissions(userData.role || 'U', [], [], [], [], false);
        }

        setStatus('success');
        showToast({
          type: 'success',
          title: UI_TEXT.SSO.CALLBACK.SUCCESS_TITLE,
          message: `Welcome, ${userData.userName || userData.userId}!`,
        });

        // Redirect to dashboard immediately
        redirectToDashboard();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to load user information';
        setStatus('error');
        setErrorMessage(message);
        showToast({
          type: 'error',
          title: UI_TEXT.SSO.CALLBACK.ERROR_TITLE,
          message:
            'Failed to load user information. Please try logging in again.',
        });
      }
    };

    handleSSOCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Error state with manual redirect button
  if (status === 'error') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Result
          status="error"
          title={UI_TEXT.SSO.CALLBACK.ERROR_TITLE}
          subTitle={errorMessage || 'An error occurred during authentication'}
          extra={
            <button
              onClick={redirectToLogin}
              style={{
                padding: '10px 24px',
                fontSize: '16px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Back to Login
            </button>
          }
        />
      </div>
    );
  }

  // Loading and success state
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '60px 40px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          minWidth: '400px',
        }}
      >
        {status === 'loading' && (
          <>
            <Spin size="large" />
            <h2 style={{ marginTop: 24, color: '#667eea' }}>
              {UI_TEXT.SSO.CALLBACK.PROCESSING_TITLE}
            </h2>
            <p style={{ color: '#666', marginTop: 8 }}>
              {UI_TEXT.SSO.CALLBACK.PROCESSING_SUBTITLE}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ fontSize: 48, color: '#52c41a' }}>✓</div>
            <h2 style={{ marginTop: 24, color: '#52c41a' }}>
              {UI_TEXT.SSO.CALLBACK.SUCCESS_TITLE}
            </h2>
            <p style={{ color: '#666', marginTop: 8 }}>
              {UI_TEXT.SSO.CALLBACK.SUCCESS_MESSAGE}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SSOCallback;
